import PASearchManager from '//99bikes.com.au/cdn/shop/t/332/assets/99-particular-audience-search-manager.js?manual=20260514&v=135908074675062598111783037908';
import {
  FilterManager,
  UIManager,
  URLManager,
  FacetRenderer,
  PaginationRenderer,
  PriceRange,
  LayoutSwitcher,
  FacetToggler
} from '//99bikes.com.au/cdn/shop/t/332/assets/99-search-constructors.js?manual=20260703&v=64672625291170894601784497962';

// Main Particular Audience Search Implementation
class PASearch {
  //Keep Algolia sort options labels and values for urls and general consistency
  static sortByOptions = [
    {value:'shopify_products',label:'Relevance'},
    {value:'shopify_products_recently_ordered_count_desc',label:'Most popular',pa:{popularity: {order: "desc", type: "number"}}},
    {value:'shopify_products_price_ratio_asc',label:'Highest discount first',pa:{discount_percent: {order: "desc", type: "number"}}},
    {value:'shopify_products_price_asc',label:'Price: Low to High',pa:{price: {order: "asc", type: "number"}}},
    {value:'shopify_products_price_desc',label:'Price: High to Low',pa:{price: {order: "desc", type: "number"}}},
    {value:'shopify_products_published_at_desc',label:'Newest first',pa:{published_at: {order: "desc", type: "number"}}},
    {value:'shopify_products_title_asc',label:'Name',pa:{title: {order: "asc", type: "text"}}}
  ];

  constructor() {
    this.paSearchManager = null;
    this.searchManager = null;
    this.filterManager = null;
    this.uiManager = null;
    this.urlManager = null;
    this.initialized = false;
    // Add pubsub unsubscriber
    this.deliveryStoreUnsubscriber = null;
    // Cache DOM elements to avoid repeated queries
    this.cachedElements = {
      sortElements: null,
      searchInput: null,
      searchResetButton: null,
      productGrid: null,
      facetContainer: null,
    };
    // Debounced methods
    this.debouncedSearch = this.debounce(this.handleSearch.bind(this), 300);
    this.paDebounceRender = this.debounce(this.paPerformRender.bind(this), 16);
    this.executeSearch = this.debounce(this.executeSearchNoDebounce.bind(this),16);
  }

  async init() {
    if (this.initialized) return;

    try {
      this.initializeComponents();
      this.setupEventListeners();
      this.setupPubSubListeners();
      await this.performInitialSearch();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Particular Audience search:', error);
    }
  }

  initializeComponents() {
    this.paSearchManager = new PASearchManager();
    this.filterManager = new FilterManager();
    this.uiManager = new UIManager();
    this.urlManager = new URLManager();

    // Load initial state from URL
    this.filterManager.initializeFromURL();
    //20260615 -PO, load initial local store filter state
    this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);

    // Populate sort options dynamically (with delay for collections)
    setTimeout(() => {
      this.populateSortOptions();
    }, 100);
  }

  cacheSortElements() {
    if (!this.cachedElements.sortElements) {
      this.cachedElements.sortElements = [
        document.getElementById('SortBy-search'),
        document.getElementById('SortBy-drawer'),
        document.getElementById('SortBy'),
        document.querySelector('select[name="sort_by"]'),
        ...document.querySelectorAll('select[data-sort-by]'),
        ...document.querySelectorAll('select[class*="sort"]'),
        ...document.querySelectorAll('select[id*="sort"]'),
        ...document.querySelectorAll('select[id*="Sort"]'),
      ]
        .filter(Boolean)
        .filter((element, index, array) => array.indexOf(element) === index);
    }
    return this.cachedElements.sortElements;
  }

  populateSortOptions() {
    const isCollection = !!window.collectionConfig;
    const sortOrders = PASearch.sortByOptions;

    if (!sortOrders || !Array.isArray(sortOrders)) {
      console.warn('Sort orders not found');
      return;
    }

    const sortElements = this.cacheSortElements();

    sortElements.forEach(selectElement => {
      // Clear existing options
      selectElement.innerHTML = '';

      // Create options in fragment first
      const tempFragment = document.createDocumentFragment();
      sortOrders.forEach(sortOrder => {
        const option = document.createElement('option');
        option.value = sortOrder.value;
        option.textContent = sortOrder.label;
        tempFragment.appendChild(option);
      });

      // Single DOM update per select
      selectElement.appendChild(tempFragment);
    });

    // Now update the selected value after options are populated
    this.filterManager.updateSortSelectors(this.filterManager.getSortBy());
  }

  cacheSearchElements() {
    if (!this.cachedElements.searchInput) {
      this.cachedElements.searchInput = document.querySelector(
        '#MainContent .search__input.form-control'
      );
    }
    if (!this.cachedElements.searchResetButton) {
      this.cachedElements.searchResetButton = document.querySelector('#MainContent .search__reset');
    }
    return {
      searchInput: this.cachedElements.searchInput,
      searchResetButton: this.cachedElements.searchResetButton,
    };
  }

  setupEventListeners() {
    const { searchInput, searchResetButton } = this.cacheSearchElements();

    // Search input with debounced handler
    if (searchInput) {
      searchInput.addEventListener('input', event => {
        this.debouncedSearch(event.target.value);
      });
    }

    if (searchResetButton) {
      searchResetButton.addEventListener('click', e => {
        e.preventDefault();
        searchInput.value = '';
        searchInput.focus();
        this.handleSearch('');
      });
    }

    // Sort elements - use cached elements
    const sortElements = this.cacheSortElements();
    sortElements.forEach(element => {
      element.addEventListener('change', event => this.handleSortChange(event.target.value));
    });

    // Use event delegation for filter changes
    this.setupGlobalEventDelegation();
  }

  setupGlobalEventDelegation() {
    // Single global event listener using delegation
    document.addEventListener(
      'change',
      event => {
        if (this.isFilterInput(event.target)) {
          this.handleFilterChange(event.target);
        }
      },
      { passive: true }
    );
  }

  setupPubSubListeners() {
    // Subscribe to delivery store updates
    this.deliveryStoreUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
      this.handleDeliveryStoreUpdate.bind(this)
    );
    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.localStoreFilterUpdated,
      this.handleLocalStoreFilterUpdate.bind(this)
    );
  }

  async handleLocalStoreFilterUpdate(e) {
    //ignore if change made from filter manager
    if (e.source === this.filterManager) return;
    try {
      this.removeClickAndCollectFilters();
      this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);
      
      //No debounce required for local store filter update
      await this.executeSearchNoDebounce();
    } catch(e) {
      console.error('Failed to handle local store filter update:', e);
    }
  }

  async handleDeliveryStoreUpdate(data) {
    try {
      // Remove any click and collect filters from URL and active filters
      this.removeClickAndCollectFilters();

      // Update current store in UI manager
      this.uiManager.updateCurrentStore();
      this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);

      // Re-execute search to refresh filters with new store
      //No debounce required for delivery store update
      await this.executeSearchNoDebounce();
    } catch (error) {
      console.error('Failed to handle delivery store update:', error);
    }
  }

  removeClickAndCollectFilters() {
    // Remove from active filters
    const activeFilters = this.filterManager.getActiveFilters();
    Object.keys(activeFilters.facets).forEach(facetName => {
      if (facetName.includes('search_information.click_and_collect')) {
        delete activeFilters.facets[facetName];
      }
    });

    // Update filter manager
    this.filterManager.activeFilters.facets = activeFilters.facets;

    // Remove from URL
    this.urlManager.removeClickAndCollectFromURL();
  }

  async performInitialSearch() {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const hasSearchTerm = searchElement?.value;
    const hasFilters = this.filterManager.hasActiveFilters();
    const isCollection = !!window.collectionConfig;
    const initialPage = this.filterManager.getInitialPage();

    if (hasSearchTerm || hasFilters || isCollection || initialPage > 0) {
      //No debounce first render
      await this.executeSearchNoDebounce(initialPage);
      // Reset initial page after first search to prevent it from persisting
      this.filterManager.resetInitialPage();
    }
  }

  async handleSearch(query) {
    await this.executeSearch();
  }

  handleSortChange(sortValue) {
    this.filterManager.setSortBy(sortValue);
    this.executeSearch();
  }

  handleFilterChange(input) {
    this.filterManager.updateFromInput(input);
    this.executeSearch();
  }

  //PA execute search will require debounce, as with filters there are currently multiple execute calls being made.
  //Algolia search client itself handled this previously
  async executeSearchNoDebounce(page = 0) {
    try {
      // Show shimmer state before starting search
      this.uiManager.showShimmerState();

      const {query, size, scope, sortFields, additionalAggregations, context} = this.paBuildSearchParams(page);
      const paResults = await this.paSearchManager.search(query,scope,sortFields,page,size,context,true,additionalAggregations);
      if (!paResults) throw new Error("Search returned no results.");

      // Use debounced rendering to prevent UI blocking
      this.paDebounceRender(paResults, page, size, context, query);
      this.urlManager.updateURL(this.filterManager.getActiveFilters(), page, this.filterManager.defaultSort);
    } catch (error) {
      console.error('Search execution failed:', error);
      // Hide shimmer state on error
      this.uiManager.hideShimmerState();
    }
  }

  paPerformRender(results, page, hitsPerPage, context, query) {
    // Use requestAnimationFrame for smooth rendering
    requestAnimationFrame(() => {
      this.uiManager.paRenderResults(results, page, hitsPerPage, context, query, this.paSearchManager?.clientId);
    });
  }

  paBuildSearchParams(page = 0) {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const query = searchElement?.value || '';

    // Get products per page from section settings
    const sectionContainer = document.querySelector('.collection[data-products-per-page]');
    const productsPerPage = sectionContainer
      ? parseInt(sectionContainer.dataset.productsPerPage)
      : 16; // fallback to default

    const sortFields = [];
    const sortByValue = this.filterManager.getSortBy();
    const sortBy = PASearch.sortByOptions.find(sort => sort.value === sortByValue);
    if (sortBy && sortBy.pa) sortFields.push({...sortBy.pa});

    //Add the current click and collect location
    const storeAggregation = this.getDeliveryStoreAggregration();

    return {
      query,
      page,
      size: productsPerPage,
      scope: this.filterManager.paBuildScope(),
      sortFields: sortFields,
      additionalAggregations: storeAggregation ? [storeAggregation] : [],
      context: this.filterManager.isCollection ? 'collection' : 'serp'
    }
  }

  getDeliveryStoreAggregration() {
    let currentStoreJSON;
    try {
      currentStoreJSON = JSON.parse(localStorage.getItem('userDeliveryStore'));
    } catch(e) {
      console.error("Search failed to parse userDeliveryStore json:",e);
      return null;
    }
    const currentStoreId = currentStoreJSON?.store_reference;
    const storeKey = window.theme.clickAndCollectLocations?.find(location => location.location_id === currentStoreId)?.status_key;
    return storeKey ? `variants.search_information.click_and_collect.${storeKey}` : null;
  }

  isFilterInput(element) {
    // Exclude price inputs since they have their own dedicated handlers
    return element.matches('input[name^="filter.v."]:not([name*="price"]), select[name*="sort"]');
  }

  debounce(func, wait = 150) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  updateSortSelectors(sortBy) {
    // Use cached sort elements
    const sortElements = this.cacheSortElements();

    // Batch DOM updates
    requestAnimationFrame(() => {
      sortElements.forEach(element => {
        if (element) element.value = sortBy;
      });
    });
  }

  // Add cleanup method
  destroy() {
    if (this.deliveryStoreUnsubscriber) {
      this.deliveryStoreUnsubscriber();
    }
  }
}

// Register custom elements
customElements.define('price-range', PriceRange);
customElements.define('layout-switcher', LayoutSwitcher);
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

// Initialize the application
document.addEventListener('DOMContentLoaded', async () => {
  window.searchProvider = new PASearch();
  await window.searchProvider.init();
});
